import { ActionType, ActionStatus, PolicyType, PolicyEscalationType, EscalationStatus, PolicyTargetStatus, QuoteStatus, PolicyState, StatementColumns } from '../models';
const action = {
    [ActionType.LATE_FEE]: {
        label: 'Invoice Late Fee',
        timelineLabel: 'late fee',
        status: {
            [ActionStatus.PENDING]: 'Pending',
            [ActionStatus.COMPLETE]: 'Issued',
            [ActionStatus.FAILED]: 'Failed',
            [ActionStatus.SUSPENDED]: 'Suspended',
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.LATE_FEE_EMAIL]: {
        label: 'Late Fee Email',
        timelineLabel: 'late fee email',
        status: {
            [ActionStatus.PENDING]: 'Pending',
            [ActionStatus.COMPLETE]: 'Sent',
            [ActionStatus.FAILED]: 'Failed',
            [ActionStatus.SUSPENDED]: 'Suspended',
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.DISCOUNT_APPLY]: {
        label: 'Apply Discount',
        timelineLabel: 'prompt payment discount',
        status: {
            [ActionStatus.PENDING]: 'Pending',
            [ActionStatus.COMPLETE]: 'Applied',
            [ActionStatus.FAILED]: 'Failed',
            [ActionStatus.SUSPENDED]: 'Suspended',
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.DISCOUNT_REMOVE]: {
        label: 'Remove Discount',
        timelineLabel: 'prompt payment discount',
        status: {
            [ActionStatus.PENDING]: 'Waiting',
            [ActionStatus.COMPLETE]: 'Removed',
            [ActionStatus.FAILED]: 'Failed',
            [ActionStatus.SUSPENDED]: 'Suspended',
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.DISCOUNT_APPLIED_EMAIL]: {
        label: 'Discount Email',
        timelineLabel: 'prompt payment email',
        status: {
            [ActionStatus.PENDING]: 'Pending',
            [ActionStatus.COMPLETE]: 'Sent',
            [ActionStatus.FAILED]: 'Failed',
            [ActionStatus.SUSPENDED]: 'Suspended',
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.REMINDER]: {
        label: 'Invoice Email',
        timelineLabel: 'invoice email',
        status: {
            [ActionStatus.PENDING]: 'Pending',
            [ActionStatus.COMPLETE]: 'Sent',
            [ActionStatus.FAILED]: 'Failed',
            [ActionStatus.SUSPENDED]: 'Suspended',
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.SEND_SMS]: {
        label: 'Text Message (SMS)',
        timelineLabel: 'SMS message',
        webhookOnlyMessage: `This action only triggered a webhook. It was not send directly to the contact.`,
        status: {
            [ActionStatus.PENDING]: 'Pending',
            [ActionStatus.COMPLETE]: 'Sent',
            [ActionStatus.FAILED]: 'Failed',
            [ActionStatus.SUSPENDED]: 'Suspended',
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.DISCOUNT_REMOVED_EMAIL]: {
        label: `Discount Removed`,
        timelineLabel: `prompt payment discount`,
        status: {
            [ActionStatus.PENDING]: `Pending`,
            [ActionStatus.FAILED]: `Failed`,
            [ActionStatus.COMPLETE]: `Sent`,
            [ActionStatus.SUSPENDED]: `Suspended`,
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.ORGANISATION_DISCONNECT]: {
        label: `Organisation Disconnection`,
        timelineLabel: `organisation disconnection`,
        status: {
            [ActionStatus.COMPLETE]: `Disconnected`,
            [ActionStatus.FAILED]: `Error`,
            [ActionStatus.PENDING]: `Pending`,
            [ActionStatus.SUSPENDED]: `Suspended`,
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.SCHEDULED_MOPUP]: {
        label: `Bulk Update`,
        timelineLabel: `bulk update`,
        status: {
            [ActionStatus.COMPLETE]: `Complete`,
            [ActionStatus.FAILED]: `Error`,
            [ActionStatus.PENDING]: `Planned`,
            [ActionStatus.SUSPENDED]: `Suspended`,
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.ISSUE_STATEMENT_LATE_FEE]: {
        label: `Check Statement Interest`,
        timelineLabel: `statement interest check`,
        status: {
            [ActionStatus.COMPLETE]: `Complete`,
            [ActionStatus.FAILED]: `Error`,
            [ActionStatus.PENDING]: `Scheduled`,
            [ActionStatus.SUSPENDED]: `Suspended`,
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.SEND_STATEMENT_INTEREST_EMAIL]: {
        label: `Statement Interest Charge Email`,
        timelineLabel: `statement interest charge email`,
        status: {
            [ActionStatus.COMPLETE]: `Sent`,
            [ActionStatus.FAILED]: `Error`,
            [ActionStatus.PENDING]: `Pending`,
            [ActionStatus.SUSPENDED]: `Suspended`,
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.SEND_ANNOUNCEMENT]: {
        label: `Announcement Email`,
        timelineLabel: `announcement email`,
        status: {
            [ActionStatus.COMPLETE]: `Sent`,
            [ActionStatus.FAILED]: `Error`,
            [ActionStatus.PENDING]: `Pending`,
            [ActionStatus.SUSPENDED]: `Suspended`,
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.SEND_STATEMENT]: {
        label: `Send Outstanding Statement`,
        timelineLabel: `outstanding statement`,
        userDescription: `On this date, we will check if this contact has any outstanding balance. If they do, we will send them a statement.`,
        status: {
            [ActionStatus.COMPLETE]: `Complete`,
            [ActionStatus.FAILED]: `Failed`,
            [ActionStatus.PENDING]: `Scheduled`,
            [ActionStatus.SUSPENDED]: `Suspended`,
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.ESCALATION]: {
        label: 'Raise Escalation',
        timelineLabel: 'escalation',
        status: {
            [ActionStatus.PENDING]: 'Pending',
            [ActionStatus.COMPLETE]: 'Scheduled',
            [ActionStatus.FAILED]: 'Failed',
            [ActionStatus.SUSPENDED]: 'Suspended',
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.AI_CALL_INVOICE]: {
        label: 'AI Phone Call',
        timelineLabel: 'AI phone call',
        status: {
            [ActionStatus.PENDING]: 'Scheduled',
            [ActionStatus.COMPLETE]: 'Queued',
            [ActionStatus.FAILED]: 'Failed',
            [ActionStatus.SUSPENDED]: 'Suspended',
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    [ActionType.QUOTE_EMAIL]: {
        label: 'Quote Email',
        timelineLabel: 'quote email',
        status: {
            [ActionStatus.PENDING]: 'Pending',
            [ActionStatus.COMPLETE]: 'Sent',
            [ActionStatus.FAILED]: 'Failed',
            [ActionStatus.SUSPENDED]: 'Suspended',
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
};
const sendgrid = {
    event: {
        'delivered': 'Delivered',
        'processed': 'Processed',
        'dropped': 'Dropped',
        'deferred': 'Deferred',
        'bounce': 'Bounced',
        'open': 'Opened',
        'click': 'Clicked',
        'spamreport': 'Spam Report',
        'unsubscribe': 'Unsubscribed',
    },
};
const generic = {
    action: {
        status: {
            [ActionStatus.PENDING]: 'Pending',
            [ActionStatus.COMPLETE]: 'Complete',
            [ActionStatus.FAILED]: 'Failed',
            [ActionStatus.SUSPENDED]: 'Suspended',
            [ActionStatus.REVERTED]: 'Reverted',
            [ActionStatus.APPROVAL_PENDING]: 'Manual (Pending)',
        },
    },
    escalation: {
        status: {
            [EscalationStatus.PENDING]: 'Open',
            [EscalationStatus.COMPLETE]: 'Closed',
            [EscalationStatus.CANCELLED]: 'Cancelled',
        },
    },
};
const escalation = {
    [PolicyEscalationType.CALL]: {
        label: '☎️ Escalate to call',
        timelineLabel: 'escalate to call',
        // TODO: add statuses here?
        status: {
            [EscalationStatus.PENDING]: 'Open',
            [EscalationStatus.COMPLETE]: 'Closed',
            [EscalationStatus.CANCELLED]: 'Cancelled',
        },
    },
    [PolicyEscalationType.STOP_CREDIT]: {
        label: '🛑 Escalate to stop credit',
        timelineLabel: 'escalate to stop credit',
        // TODO: add statuses here?
        status: {
            [EscalationStatus.PENDING]: 'Open',
            [EscalationStatus.COMPLETE]: 'Closed',
            [EscalationStatus.CANCELLED]: 'Cancelled',
        },
    },
    [PolicyEscalationType.LETTER]: {
        label: '✉️ Escalate to letter',
        timelineLabel: 'escalate to letter',
        // TODO: add statuses here?
        status: {
            [EscalationStatus.PENDING]: 'Open',
            [EscalationStatus.COMPLETE]: 'Closed',
            [EscalationStatus.CANCELLED]: 'Cancelled',
        },
    },
};
const policy = {
    targetStatus: {
        [PolicyTargetStatus.DUE]: 'due date',
        [PolicyTargetStatus.ISSUED]: 'issue date',
        [PolicyTargetStatus.MARKED_PAID]: 'date marked paid',
        [PolicyTargetStatus.EXPIRY_DATE]: 'expiry date',
    },
    verb: {
        [PolicyType.SMS]: `sms notification`,
        [PolicyType.REMINDER]: `email`,
        [PolicyType.ESCALATION]: `escalation`,
        [PolicyType.AI_CALL]: `AI phone call`,
        [PolicyType.QUOTE_EMAIL]: `quote email`,
        [PolicyType.LATE_FEE]: `late fee`,
        [PolicyType.STATEMENT_LATE_FEE]: `statement interest charge`,
        [PolicyType.STATEMENT]: `statement`,
        [PolicyType.DISCOUNT]: `prompt payment discount`,
    },
    state: {
        [PolicyState.ACTIVE]: {
            label: `Auto`,
            tooltip: `Automatically run this policy`,
        },
        [PolicyState.INACTIVE]: {
            label: `Off`,
            tooltip: `The policy will not create any actions`,
        },
        [PolicyState.APPROVAL]: {
            label: `Manual`,
            tooltip: `Actions created by this policy will NOT be automatically ran.`,
        },
    },
};
const aiAnalysis = {
    hasAlreadyPaid: 'Has already paid',
    willPayDate: 'Will pay on',
    incorrectContact: 'Wrong person or number',
    requiresResend: 'Requires resend',
    requiresHumanCall: 'Requires human call',
    unhappyCustomer: `Customer mood`,
};
const statementColSettings = {
    [StatementColumns.Activity]: {
        label: `Activity`,
    },
    [StatementColumns.Amount]: {
        label: `Amount`,
    },
    [StatementColumns.Balance]: {
        label: `Balance`,
    },
    [StatementColumns.Date]: {
        label: `Date`,
    },
    [StatementColumns.DueDate]: {
        label: `Due Date`,
    },
    [StatementColumns.OverdueAge]: {
        label: `Overdue Age`,
    },
    [StatementColumns.Payments]: {
        label: `Payments`,
    },
    [StatementColumns.Reference]: {
        label: `Reference`,
    },
};
const settings = {
    statement: {
        statementColumnSettings: statementColSettings,
    },
};
const quotes = {
    status: {
        [QuoteStatus.DRAFT]: 'Draft',
        [QuoteStatus.SENT]: 'Sent',
        [QuoteStatus.ACCEPTED]: 'Accepted',
        [QuoteStatus.DECLINED]: 'Declined',
        [QuoteStatus.DELETED]: 'Deleted',
        [QuoteStatus.UNKNOWN]: 'Unknown',
        [QuoteStatus.CONVERTED]: 'Converted',
    },
};
export const langauge = {
    policyType: {
        [PolicyType.LATE_FEE]: 'Invoice Late Fee',
        [PolicyType.STATEMENT_LATE_FEE]: 'Statement Interest Charge',
        [PolicyType.STATEMENT]: 'Auto Statement',
        [PolicyType.DISCOUNT]: 'Prompt Payment Discount',
        [PolicyType.REMINDER]: 'Email',
        [PolicyType.SMS]: 'SMS Alert',
        [PolicyType.ESCALATION]: 'Escalation',
        [PolicyType.AI_CALL]: 'AI Phone Call',
        [PolicyType.QUOTE_EMAIL]: 'Quote Email',
    },
    policy,
    action,
    escalation,
    sendgrid,
    generic,
    aiAnalysis,
    quotes,
    settings,
};
//# sourceMappingURL=language.js.map